<template>
    <transition name="gallery-fade">
        <div v-if="visible" class="chat-media-gallery-overlay">
            <div class="gallery-backdrop" @click="tryCloseFromBackdrop"></div>
            <div class="gallery-panel" @click.stop>
            <div class="gallery-header">
                <span class="gallery-counter">{{ currentIndex + 1 }} / {{ items.length }}</span>
                <div class="gallery-header-actions">
                    <button
                        v-if="showGoToMessage && canEmitGoToMessage"
                        type="button"
                        class="gallery-go-message-btn"
                        v-tippy="{ followCursor: true, placement: 'bottom' }"
                        :content="$t('task.goToMessage')"
                        @click.stop="emitGoToMessage"
                    >
                        <span class="material-icons">my_location</span>
                        <span class="gallery-go-message-label">{{ $t("task.goToMessage") }}</span>
                    </button>
                    <button type="button" class="gallery-close-btn" @click="close">
                        <span class="material-icons">close</span>
                    </button>
                </div>
            </div>

            <div class="gallery-body">
                <button class="gallery-nav-btn gallery-prev" :disabled="currentIndex === 0" @click="prev">
                    <span class="material-icons">chevron_left</span>
                </button>

                <div
                    class="gallery-media-container"
                    @wheel.prevent="onWheel"
                    @mousedown="onDragStart"
                    @mousemove="onDragMove"
                    @mouseup="onDragEnd"
                    @mouseleave="onDragEnd"
                >
                    <template v-if="currentItem">
                        <div v-if="imageLoading && isItemImage(currentItem)" class="gallery-loading">
                            <span class="material-icons gallery-loading-icon">hourglass_empty</span>
                        </div>
                        <video
                            v-if="isItemVideo(currentItem)"
                            :key="'vid-' + currentIndex"
                            :src="currentItem.url"
                            controls
                            autoplay
                            class="gallery-video"
                        ></video>
                        <div v-else-if="isItemAudio(currentItem)" :key="'aud-' + currentIndex" class="gallery-audio-panel">
                            <span class="material-icons gallery-audio-panel-icon">mic</span>
                            <p class="gallery-audio-panel-name">{{ itemDisplayName(currentItem) }}</p>
                            <div class="gallery-audio-panel-player">
                                <audio
                                    :src="currentItem.url"
                                    controls
                                    preload="auto"
                                    class="gallery-audio"
                                ></audio>
                            </div>
                        </div>
                        <img
                            v-else-if="isItemImage(currentItem)"
                            ref="galleryMainImg"
                            :key="'img-' + currentIndex + '-' + galleryImageKeySuffix(currentItem)"
                            :src="currentItem.url"
                            class="gallery-img"
                            :class="{ 'gallery-img--hidden': imageLoading, 'gallery-img--dragging': dragging }"
                            :style="imgTransformStyle"
                            :data-gallery-load-token="String(imageLoadToken)"
                            @load="onImageLoad"
                            @error="onImageError"
                            draggable="false"
                        />
                        <div v-else :key="'file-' + currentIndex" class="gallery-file-fallback">
                            <span class="material-icons gallery-file-fallback-icon">insert_drive_file</span>
                            <p class="gallery-file-name">{{ itemDisplayName(currentItem) }}</p>
                            <a
                                class="gallery-file-download"
                                :href="currentItem.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                            >
                                {{ $t("message.download") }}
                            </a>
                        </div>
                    </template>
                </div>

                <button
                    class="gallery-nav-btn gallery-next"
                    :disabled="currentIndex === items.length - 1"
                    @click="next"
                >
                    <span class="material-icons">chevron_right</span>
                </button>
            </div>

            <div v-if="currentItem && isItemImage(currentItem)" class="gallery-toolbar">
                <button type="button" class="gallery-toolbar-btn" @click="zoomIn" v-tippy="{ placement: 'top' }" content="Zoom In">
                    <span class="material-icons">add</span>
                </button>
                <button type="button" class="gallery-toolbar-btn" @click="zoomOut" v-tippy="{ placement: 'top' }" content="Zoom Out">
                    <span class="material-icons">remove</span>
                </button>
                <button type="button" class="gallery-toolbar-btn" @click="zoomReset" v-tippy="{ placement: 'top' }" content="1:1">
                    <span class="gallery-toolbar-label">1:1</span>
                </button>
                <span class="gallery-toolbar-divider"></span>
                <button type="button" class="gallery-toolbar-btn" @click="rotateLeft" v-tippy="{ placement: 'top' }" content="Rotate Left">
                    <span class="material-icons">rotate_left</span>
                </button>
                <button type="button" class="gallery-toolbar-btn" @click="rotateRight" v-tippy="{ placement: 'top' }" content="Rotate Right">
                    <span class="material-icons">rotate_right</span>
                </button>
                <span class="gallery-toolbar-divider"></span>
                <button type="button" class="gallery-toolbar-btn" @click="flipHorizontal" v-tippy="{ placement: 'top' }" content="Flip Horizontal">
                    <span class="material-icons">swap_horiz</span>
                </button>
                <button type="button" class="gallery-toolbar-btn" @click="flipVertical" v-tippy="{ placement: 'top' }" content="Flip Vertical">
                    <span class="material-icons">swap_vert</span>
                </button>
                <span class="gallery-toolbar-divider"></span>
                <button type="button" class="gallery-toolbar-btn" @click="downloadCurrent" v-tippy="{ placement: 'top' }" content="Download">
                    <span class="material-icons">file_download</span>
                </button>
            </div>

            <div class="gallery-thumbnails" ref="galleryThumbs">
                <div
                    v-for="entry in visibleThumbs"
                    :key="entry.idx"
                    class="gallery-thumb"
                    :class="{
                        active: entry.idx === currentIndex,
                        'gallery-thumb--audio': isItemAudio(entry.item),
                    }"
                    @click="currentIndex = entry.idx"
                >
                    <img v-if="isItemImage(entry.item)" :src="entry.item.url" loading="lazy" />
                    <div v-else-if="isItemVideo(entry.item)" class="gallery-thumb-video-placeholder">
                        <span class="material-icons">play_circle_filled</span>
                    </div>
                    <div v-else-if="isItemAudio(entry.item)" class="gallery-thumb-audio-card">
                        <span class="material-icons gallery-thumb-audio-card-icon">graphic_eq</span>
                        <audio
                            class="gallery-thumb-audio-mini"
                            :src="entry.item.url"
                            controls
                            preload="metadata"
                            @click.stop
                        ></audio>
                    </div>
                    <div v-else class="gallery-thumb-file-placeholder">
                        <span class="material-icons">insert_drive_file</span>
                    </div>
                </div>
            </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: "ChatMediaGallery",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: function () {
                return [];
            },
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
        showGoToMessage: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            currentIndex: 0,
            imageLoadToken: 0,
            imageLoading: false,
            imgZoom: 1,
            imgRotate: 0,
            imgFlipH: false,
            imgFlipV: false,
            imgPanX: 0,
            imgPanY: 0,
            dragging: false,
            dragStartX: 0,
            dragStartY: 0,
            dragStartPanX: 0,
            dragStartPanY: 0,
            imgNatW: 0,
            imgNatH: 0,
            suppressBackdropCloseUntil: 0,
            galleryDocMouseUpHandler: null,
        };
    },
    computed: {
        currentItem: function () {
            return this.items[this.currentIndex] || null;
        },
        isRotatedSideways: function () {
            var r = ((this.imgRotate % 360) + 360) % 360;
            return r === 90 || r === 270;
        },
        rotateAutoFitScale: function () {
            if (!this.isRotatedSideways) return 1;
            if (!this.imgNatW || !this.imgNatH) return 1;
            var container = this.$el && this.$el.querySelector(".gallery-media-container");
            if (!container) return 1;
            var cW = container.clientWidth || 1;
            var cH = container.clientHeight || 1;
            var fitW = Math.min(this.imgNatW, cW);
            var fitH = Math.min(this.imgNatH, cH);
            var needScaleW = fitH > cW ? cW / fitH : 1;
            var needScaleH = fitW > cH ? cH / fitW : 1;
            return Math.min(needScaleW, needScaleH);
        },
        imgTransformStyle: function () {
            var parts = [];
            if (this.imgPanX !== 0 || this.imgPanY !== 0) {
                parts.push("translate(" + this.imgPanX + "px, " + this.imgPanY + "px)");
            }
            var effectiveZoom = this.imgZoom * this.rotateAutoFitScale;
            if (effectiveZoom !== 1) {
                parts.push("scale(" + effectiveZoom + ")");
            }
            if (this.imgRotate !== 0) {
                parts.push("rotate(" + this.imgRotate + "deg)");
            }
            var scaleX = this.imgFlipH ? -1 : 1;
            var scaleY = this.imgFlipV ? -1 : 1;
            if (scaleX !== 1 || scaleY !== 1) {
                parts.push("scale(" + scaleX + ", " + scaleY + ")");
            }
            if (!parts.length) return {};
            return { transform: parts.join(" ") };
        },
        canEmitGoToMessage: function () {
            var item = this.currentItem;
            return !!(item && (item.chatMessage || item.goToMessage));
        },
        visibleThumbs: function () {
            var windowSize = 20;
            var start = Math.max(0, this.currentIndex - windowSize);
            var end = Math.min(this.items.length - 1, this.currentIndex + windowSize);
            var result = [];
            var i;
            for (i = start; i <= end; i++) {
                result.push({ item: this.items[i], idx: i });
            }
            return result;
        },
    },
    watch: {
        visible: function (val) {
            if (val) {
                var max = Math.max(0, this.items.length - 1);
                var idx = this.initialIndex;
                if (idx < 0) idx = 0;
                if (idx > max) idx = max;
                this.currentIndex = idx;
                this.syncImageLoading();
            } else {
                this.imageLoading = false;
                this.removeGalleryDocMouseUp();
            }
        },
        initialIndex: function () {
            if (this.visible && this.items.length) {
                var max = Math.max(0, this.items.length - 1);
                var idx = this.initialIndex;
                if (idx < 0) idx = 0;
                if (idx > max) idx = max;
                this.currentIndex = idx;
                this.syncImageLoading();
            }
        },
        currentIndex: function () {
            this.resetImgTransform();
            this.syncImageLoading();
            var self = this;
            this.$nextTick(function () {
                var root = self.$refs.galleryThumbs;
                var activeThumb = root && root.querySelector(".gallery-thumb.active");
                if (activeThumb) {
                    activeThumb.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
                }
            });
        },
        items: function () {
            if (this.visible) {
                var max = Math.max(0, this.items.length - 1);
                if (this.currentIndex > max) this.currentIndex = max;
                this.syncImageLoading();
            }
        },
    },
    mounted: function () {
        document.addEventListener("keydown", this.onKeydown);
    },
    beforeDestroy: function () {
        document.removeEventListener("keydown", this.onKeydown);
        this.removeGalleryDocMouseUp();
    },
    methods: {
        itemExt: function (item) {
            if (item && item.ext) return String(item.ext).toLowerCase();
            var name = item && item.name ? String(item.name) : "";
            if (!name || name.indexOf(".") === -1) return "";
            return name
                .split(".")
                .pop()
                .toLowerCase();
        },
        itemMime: function (item) {
            return item && item.mime ? String(item.mime).toLowerCase() : "";
        },
        isItemVideo: function (item) {
            if (!item || !item.url) return false;
            var mime = this.itemMime(item);
            var ext = this.itemExt(item);
            if (mime.indexOf("audio") !== -1 && mime.indexOf("video") === -1) return false;
            var videoExts = ["mp4", "webm", "mov", "avi", "mkv", "m4v"];
            return (
                mime.indexOf("video") !== -1 ||
                videoExts.indexOf(ext) !== -1 ||
                item.messageType === "video"
            );
        },
        isItemAudio: function (item) {
            if (!item || !item.url) return false;
            if (this.isItemVideo(item)) return false;
            if (item.messageType === "audio") return true;
            var mime = this.itemMime(item);
            var ext = this.itemExt(item);
            if (mime.indexOf("audio") !== -1) return true;
            var tokens = [
                "mp3",
                "ogg",
                "oga",
                "opus",
                "wav",
                "flac",
                "m4a",
                "aac",
                "amr",
                "mpeg",
                "x-m4a",
                "x-caf",
                "3gpp",
            ];
            var i;
            for (i = 0; i < tokens.length; i++) {
                var t = tokens[i];
                if (mime.indexOf(t) !== -1) return true;
                if (ext.indexOf(t) !== -1) return true;
            }
            return false;
        },
        isItemImage: function (item) {
            if (!item || !item.url) return false;
            if (this.isItemVideo(item) || this.isItemAudio(item)) return false;
            if (item.messageType === "image") return true;
            var mime = this.itemMime(item);
            var ext = this.itemExt(item);
            var imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "ico"];
            if (mime.indexOf("image") !== -1) return true;
            if (imageExts.indexOf(ext) !== -1) return true;
            return false;
        },
        itemDisplayName: function (item) {
            if (!item) return "";
            return item.name || "Dosya";
        },
        syncImageLoading: function () {
            var item = this.items[this.currentIndex];
            if (item && this.isItemImage(item)) {
                this.imageLoadToken++;
                this.imageLoading = true;
                var self = this;
                var seq = this.imageLoadToken;
                this.$nextTick(function () {
                    self.verifyCachedImageForToken(seq);
                });
                if (typeof window !== "undefined" && window.requestAnimationFrame) {
                    window.requestAnimationFrame(function () {
                        window.requestAnimationFrame(function () {
                            self.verifyCachedImageForToken(seq);
                        });
                    });
                }
            } else {
                this.imageLoading = false;
            }
        },
        galleryImageKeySuffix: function (item) {
            if (!item || !item.url) return "0";
            var u = String(item.url);
            var h = 0;
            var i;
            for (i = 0; i < u.length; i++) {
                h = (h * 31 + u.charCodeAt(i)) | 0;
            }
            return String(h);
        },
        verifyCachedImageForToken: function (seq) {
            if (seq !== this.imageLoadToken) return;
            if (!this.visible) return;
            var item = this.items[this.currentIndex];
            if (!item || !this.isItemImage(item)) return;
            var img = this.$refs.galleryMainImg;
            if (!img) return;
            var tok = img.getAttribute("data-gallery-load-token");
            if (tok !== String(this.imageLoadToken)) return;
            if (img.complete && img.naturalHeight > 0) {
                this.onImageLoad({ target: img });
            }
        },
        tryCloseFromBackdrop: function () {
            if (Date.now() < this.suppressBackdropCloseUntil) return;
            this.close();
        },
        close: function () {
            this.removeGalleryDocMouseUp();
            this.imageLoading = false;
            this.$emit("update:visible", false);
        },
        removeGalleryDocMouseUp: function () {
            if (this.galleryDocMouseUpHandler) {
                document.removeEventListener("mouseup", this.galleryDocMouseUpHandler, true);
                this.galleryDocMouseUpHandler = null;
            }
        },
        prev: function () {
            if (this.currentIndex > 0) this.currentIndex--;
        },
        next: function () {
            if (this.currentIndex < this.items.length - 1) this.currentIndex++;
        },
        onKeydown: function (e) {
            if (!this.visible) return;
            if (e.key === "ArrowLeft") this.prev();
            else if (e.key === "ArrowRight") this.next();
            else if (e.key === "Escape") this.close();
            else if (e.key === "+" || e.key === "=") this.zoomIn();
            else if (e.key === "-") this.zoomOut();
            else if (e.key === "0") this.zoomReset();
        },
        onImageLoad: function (e) {
            var img = e && e.target;
            if (!img) return;
            var tok = img.getAttribute("data-gallery-load-token");
            if (tok !== String(this.imageLoadToken)) return;
            this.imageLoading = false;
            this.imgNatW = img.naturalWidth || 0;
            this.imgNatH = img.naturalHeight || 0;
        },
        onImageError: function (e) {
            var img = e && e.target;
            if (!img) return;
            var tok = img.getAttribute("data-gallery-load-token");
            if (tok !== String(this.imageLoadToken)) return;
            this.imageLoading = false;
        },
        resetImgTransform: function () {
            this.removeGalleryDocMouseUp();
            this.imgZoom = 1;
            this.imgRotate = 0;
            this.imgFlipH = false;
            this.imgFlipV = false;
            this.imgPanX = 0;
            this.imgPanY = 0;
            this.dragging = false;
            this.imgNatW = 0;
            this.imgNatH = 0;
        },
        zoomIn: function () {
            this.imgZoom = Math.min(this.imgZoom * 1.25, 10);
        },
        zoomOut: function () {
            var next = this.imgZoom / 1.25;
            if (next < 0.1) next = 0.1;
            this.imgZoom = next;
            if (this.imgZoom <= 1) {
                this.imgPanX = 0;
                this.imgPanY = 0;
            }
        },
        zoomReset: function () {
            this.imgZoom = 1;
            this.imgPanX = 0;
            this.imgPanY = 0;
        },
        rotateLeft: function () {
            this.imgRotate = (this.imgRotate - 90) % 360;
        },
        rotateRight: function () {
            this.imgRotate = (this.imgRotate + 90) % 360;
        },
        flipHorizontal: function () {
            this.imgFlipH = !this.imgFlipH;
        },
        flipVertical: function () {
            this.imgFlipV = !this.imgFlipV;
        },
        onWheel: function (e) {
            if (!this.currentItem || !this.isItemImage(this.currentItem)) return;
            if (e.deltaY < 0) {
                this.zoomIn();
            } else {
                this.zoomOut();
            }
        },
        onDragStart: function (e) {
            if (!this.currentItem || !this.isItemImage(this.currentItem)) return;
            if (this.imgZoom <= 1) return;
            this.removeGalleryDocMouseUp();
            this.dragging = true;
            this.dragStartX = e.clientX;
            this.dragStartY = e.clientY;
            this.dragStartPanX = this.imgPanX;
            this.dragStartPanY = this.imgPanY;
            var self = this;
            this.galleryDocMouseUpHandler = function () {
                self.onDragEnd();
            };
            document.addEventListener("mouseup", this.galleryDocMouseUpHandler, true);
        },
        onDragMove: function (e) {
            if (!this.dragging) return;
            this.imgPanX = this.dragStartPanX + (e.clientX - this.dragStartX);
            this.imgPanY = this.dragStartPanY + (e.clientY - this.dragStartY);
        },
        onDragEnd: function () {
            this.removeGalleryDocMouseUp();
            if (this.dragging) {
                this.suppressBackdropCloseUntil = Date.now() + 500;
            }
            this.dragging = false;
        },
        downloadCurrent: function () {
            var item = this.currentItem;
            if (!item || !item.url) return;
            var a = document.createElement("a");
            a.href = item.url;
            a.download = item.name || "download";
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        emitGoToMessage: function () {
            var item = this.items[this.currentIndex];
            if (!item) return;
            if (item.chatMessage) {
                this.$emit("go-to-message", { message: item.chatMessage });
                return;
            }
            if (item.goToMessage) {
                this.$emit("go-to-message", { scrollTarget: item.goToMessage });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.chat-media-gallery-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99999;
    background: rgba(0, 0, 0, 0.93);

    .gallery-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        cursor: pointer;
    }

    .gallery-panel {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        height: 100%;
        min-height: 0;
        pointer-events: none;

        > * {
            pointer-events: auto;
        }
    }

    .gallery-panel .gallery-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 20px;
        color: #fff;
        gap: 12px;

        .gallery-counter {
            font-size: 14px;
            opacity: 0.85;
            flex-shrink: 0;
        }

        .gallery-header-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .gallery-go-message-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            background: rgba(255, 255, 255, 0.12);
            border: none;
            border-radius: 20px;
            padding: 6px 12px 6px 10px;
            cursor: pointer;
            transition: background 0.2s;
            color: #fff;
            font-size: 13px;

            .material-icons {
                color: #fff;
                font-size: 20px;
            }

            .gallery-go-message-label {
                white-space: nowrap;
            }

            &:hover {
                background: rgba(255, 255, 255, 0.25);
            }
        }

        .gallery-close-btn {
            background: rgba(255, 255, 255, 0.12);
            border: none;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.2s;

            .material-icons {
                color: #fff;
                font-size: 20px;
            }

            &:hover {
                background: rgba(255, 255, 255, 0.25);
            }
        }
    }

    .gallery-panel .gallery-body {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        overflow: hidden;
        padding: 0 8px;

        .gallery-nav-btn {
            flex-shrink: 0;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 50%;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.2s;

            .material-icons {
                color: #fff;
                font-size: 28px;
            }

            &:hover:not(:disabled) {
                background: rgba(255, 255, 255, 0.25);
            }

            &:disabled {
                opacity: 0.25;
                cursor: default;
            }
        }

            .gallery-media-container {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            max-height: calc(100vh - 200px);
            overflow: hidden;
            position: relative;

            .gallery-loading {
                position: absolute;
                display: flex;
                align-items: center;
                justify-content: center;

                .gallery-loading-icon {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 40px;
                    animation: gallery-spin 1s linear infinite;
                }
            }

            .gallery-img {
                max-width: 100%;
                max-height: calc(100vh - 200px);
                object-fit: contain;
                border-radius: 4px;
                transition: opacity 0.2s, transform 0.15s ease;
                transform-origin: center center;
                user-select: none;

                &.gallery-img--hidden {
                    opacity: 0;
                }

                &.gallery-img--dragging {
                    cursor: grabbing;
                    transition: opacity 0.2s;
                }
            }

            .gallery-video {
                max-width: 100%;
                max-height: calc(100vh - 160px);
                border-radius: 4px;
                outline: none;
            }

            .gallery-audio-panel {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 12px;
                max-width: 520px;
                width: 100%;
                padding: 8px;

                .gallery-audio-panel-icon {
                    font-size: 48px;
                    color: rgba(255, 255, 255, 0.9);
                }

                .gallery-audio-panel-name {
                    margin: 0;
                    font-size: 14px;
                    color: rgba(255, 255, 255, 0.92);
                    text-align: center;
                    word-break: break-word;
                    max-width: 100%;
                }

                .gallery-audio-panel-player {
                    width: 100%;
                    background: #e8eaed;
                    border-radius: 12px;
                    padding: 12px 14px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
                }

                .gallery-audio {
                    width: 100%;
                    min-width: 260px;
                    height: 48px;
                    display: block;
                }
            }

            .gallery-file-fallback {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 12px;
                padding: 24px;
                color: #fff;
                text-align: center;

                .gallery-file-fallback-icon {
                    font-size: 64px;
                    opacity: 0.75;
                }

                .gallery-file-name {
                    margin: 0;
                    font-size: 14px;
                    max-width: 90vw;
                    word-break: break-word;
                }

                .gallery-file-download {
                    color: #5eb8ff;
                    text-decoration: underline;
                    font-size: 15px;
                }
            }
        }
    }

    .gallery-panel .gallery-toolbar {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        padding: 6px 12px;
        flex-shrink: 0;

        .gallery-toolbar-btn {
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.15s;
            color: #fff;

            .material-icons {
                font-size: 20px;
                color: #fff;
            }

            .gallery-toolbar-label {
                font-size: 13px;
                font-weight: 600;
                color: #fff;
                line-height: 1;
            }

            &:hover {
                background: rgba(255, 255, 255, 0.25);
            }

            &:active {
                background: rgba(255, 255, 255, 0.35);
            }
        }

        .gallery-toolbar-divider {
            width: 1px;
            height: 20px;
            background: rgba(255, 255, 255, 0.2);
            margin: 0 4px;
        }
    }

    .gallery-panel .gallery-thumbnails {
        display: flex;
        flex-direction: row;
        gap: 6px;
        padding: 10px 16px;
        overflow-x: auto;
        background: rgba(0, 0, 0, 0.4);
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.3) transparent;

        &::-webkit-scrollbar {
            height: 4px;
        }
        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
        }

        .gallery-thumb {
            flex-shrink: 0;
            width: 56px;
            height: 56px;
            border-radius: 4px;
            overflow: hidden;
            cursor: pointer;
            border: 2px solid transparent;
            transition: border-color 0.15s;
            background: #333;

            &.gallery-thumb--audio {
                width: 96px;
                height: auto;
                min-height: 58px;
                max-height: 76px;
                overflow: visible;
                padding: 2px;
                background: transparent;
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            .gallery-thumb-video-placeholder {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #222;

                .material-icons {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 28px;
                }
            }

            .gallery-thumb-audio-card {
                width: 100%;
                height: 100%;
                min-height: 54px;
                display: flex;
                flex-direction: column;
                align-items: stretch;
                justify-content: flex-start;
                gap: 2px;
                background: #e8eaed;
                border-radius: 4px;
                padding: 4px 4px 2px;
                box-sizing: border-box;

                .gallery-thumb-audio-card-icon {
                    font-size: 16px;
                    color: #5f6368;
                    align-self: center;
                    line-height: 1;
                }

                .gallery-thumb-audio-mini {
                    width: 100%;
                    height: 32px;
                    display: block;
                }
            }

            .gallery-thumb-file-placeholder {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #2a2a2a;

                .material-icons {
                    color: rgba(255, 255, 255, 0.65);
                    font-size: 26px;
                }
            }

            &.active {
                border-color: #fff;
            }

            &:hover:not(.active) {
                border-color: rgba(255, 255, 255, 0.5);
            }
        }
    }
}

.gallery-fade-enter-active,
.gallery-fade-leave-active {
    transition: opacity 0.2s;
}
.gallery-fade-enter,
.gallery-fade-leave-to {
    opacity: 0;
}

@keyframes gallery-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
